import { z } from "zod";
import { nlWkrCategorySchema } from "./nlWkr.js";
export const linkedEntityTypeEnum = z.enum([
    "expense",
    "ap_invoice",
    "ar_invoice",
]);
/**
 * Which key a history-driven category recommendation matched on — the
 * `reason` column of `recommend_import_categories` and the CHECK on
 * `accounting_imports.suggestion_reason`. Lives here (not in clearingReads.ts,
 * which already imports from this file) so the provenance schema below can
 * consume it without a module cycle; clearingReads.ts types its
 * `CategoryRecommendation.reason` from the same enum.
 */
export const categoryRecommendationReasonEnum = z.enum([
    "iban",
    "vendor",
    "vendor_text",
    "text",
]);
/**
 * Every value `accounting_imports.suggestion_reason` admits: the recommender's
 * four tiers plus `'propagated'` — a CLIENT-asserted reason (the user staged
 * a category on a same-payee row and applied it to this one via the index
 * view's `counterparty_key`). The recommender never emits `'propagated'`, so
 * `CategoryRecommendation.reason` stays on the narrow enum above.
 */
export const suggestionReasonEnum = z.enum([
    ...categoryRecommendationReasonEnum.options,
    "propagated",
]);
/**
 * The recommendation that was on a bank-feed row when the user committed it —
 * accepted or not. `/apply` and `/add-expense` forward it verbatim as
 * `p_suggestion`, and the RPCs stamp it onto `accounting_imports`
 * (`suggested_account_id` / `suggestion_reason` / `suggestion_confidence`) so
 * acceptance can be measured (`supabase/diagnostics/category_suggestion_outcomes.sql`).
 * Absent/null means the row had no recommendation. `.strict()` mirrors the
 * two DB CHECKs: a value outside them is a clean 400, never a Postgres error.
 * `confidence` is `null` for a `'propagated'` stamp — there is no recommender
 * verdict to record (`'strong'` would be fabricated); the DB column is
 * independently nullable.
 */
export const suggestionProvenanceSchema = z
    .object({
    account_id: z.string().uuid(),
    reason: suggestionReasonEnum,
    confidence: z.enum(["strong", "weak"]).nullable(),
})
    .strict();
export const applyAccountingImportSchema = z
    .object({
    offset_account_id: z.string().uuid(),
    description: z.string().nullable().optional(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    /** Suggestion provenance stamp — see {@link suggestionProvenanceSchema}. */
    suggestion: suggestionProvenanceSchema.nullable().optional(),
})
    .strict();
export const matchAccountingImportSchema = z
    .object({
    transaction_id: z.string().uuid(),
})
    .strict();
export const linkAccountingImportSchema = z
    .object({
    transaction_id: z.string().uuid(),
    entity_type: linkedEntityTypeEnum,
})
    .strict();
// ---------- skip_accounting_import ----------
/**
 * Why a bank line was skipped. The column is
 * `accounting_imports.skip_reason text NULL CHECK (skip_reason IS NULL OR
 * skip_reason = 'duplicate')`, so this enum is the whole vocabulary.
 *
 * NULL (absent on the wire) is NOT a member and is NOT a hole: it is the
 * pre-existing skip — "not for the books" — and stays inside the bridge's
 * `bank_balance` / `skipped_feed` / `import_count`. `'duplicate'` means the
 * line is a second copy of a statement row already in the feed, so an
 * UNLINKED duplicate is dropped from those three figures
 * (`get_feed_account_clearing_summary`). There is deliberately no `'other'`
 * value: it would only rename today's Skip and turn the deploy window into a
 * NULL-rejection trap.
 */
export const skipReasonEnum = z.enum(["duplicate"]);
/**
 * How a loose duplicate pair was decided, strongest first — the value the
 * sync tallies per FETCH_TRANSACTIONS audit row, the grid's duplicate chip
 * explains in its tooltip, and `accounting_imports.duplicate_evidence` stores
 * (CHECK via `is_loose_duplicate_evidence()`; the SQL predicate and this enum
 * are the two homes of the seven-value list). The rules themselves — what
 * each value means and how they rank — are documented on
 * `bankImport/looseDuplicate.ts`, which re-exports the type and derives
 * `LOOSE_DUPLICATE_EVIDENCE_VALUES` from `.options`.
 *
 * Lives here, not in looseDuplicate.ts, for the same reason as
 * `categoryRecommendationReasonEnum`: this is the enum home (like
 * `skipReasonEnum`), the provenance schema below consumes it, and
 * clearingReads.ts imports from this file while looseDuplicate.ts type-imports
 * from clearingReads.ts — so no value-level cycle.
 */
export const looseDuplicateEvidenceEnum = z.enum([
    "bank_id",
    "reference",
    "end_to_end",
    "end_to_end_token",
    "counterparty_account",
    "remittance_text",
    "key_only",
]);
/**
 * Provenance of a duplicate skip: WHICH feed row this one is a copy of and
 * WHICH loose rule decided it. Sent by the web when the user accepts a
 * "Duplicate of …" chip (or clicks "Skip suggested (N)"), forwarded as
 * `p_duplicate` to `skip_accounting_import`, and stamped onto
 * `accounting_imports.duplicate_of_import_id` / `duplicate_evidence`. Unlike
 * the category stamp ({@link suggestionProvenanceSchema}, a measurement), this
 * pair is RENDERED (the expanded row says "Skipped as duplicate of …") and
 * claims a relation inside one feed, so the RPC checks that `of_import_id`
 * names an RLS-visible row of the same org AND the same `bank_account_id`
 * (`invalid_duplicate` otherwise). No FK on purpose — the partner may later be
 * un-imported by `remove_import_batch`; readers tolerate a dangling id.
 * `undo_accounting_import` clears both columns: an Undo'd copy re-enters the
 * live suggestion engine, and a stale stamp beside a live chip would be two
 * answers for one row.
 */
export const duplicateProvenanceSchema = z
    .object({
    of_import_id: z.string().uuid(),
    evidence: looseDuplicateEvidenceEnum,
})
    .strict();
/**
 * Request body for POST /api/v1/clearing/orgs/:orgId/imports/:id/skip.
 *
 * `skip_reason` is optional and the WHOLE BODY IS OPTIONAL: a web build from
 * before this release calls the endpoint with no `json` at all, hence no
 * Content-Type, and hono@4.12.18's validator leaves `value = {}` in that case
 * (`node_modules/hono/dist/validator/validator.js:10-16` — `let value = {}`,
 * then `case "json": if (!contentType || !jsonRegex.test(contentType)) break;`
 * so the body is never read). `{}` satisfies this schema, `c.req.valid("json")`
 * returns `{}`, and the route sends `p_skip_reason: undefined` → the RPC's
 * DEFAULT NULL → today's behaviour. That is version-specific (early 4.x threw
 * on a missing Content-Type); the lockfile pins it and clearing.test.ts's
 * "no body" case guards it.
 *
 * A JSON Content-Type with an EMPTY body is still a 400 "Malformed JSON in
 * request body" from hono itself — no client sends that shape.
 *
 * `.strict()` so an unknown key (or a reason outside the DB CHECK) is a clean
 * 400 at the boundary instead of a constraint violation in Postgres.
 *
 * `duplicate` ({@link duplicateProvenanceSchema}) is optional too and only
 * legal beside `skip_reason: 'duplicate'` — the `.refine` mirrors the DB's
 * `accounting_imports_duplicate_provenance_skip_check`, so a half-formed body
 * is a 400 here rather than a 23514 (or the RPC's `invalid_duplicate`) later.
 * A menu "Skip as duplicate" with no chip sends the reason alone and stays
 * byte-identical to before; a reason-less Skip stays body-less. The `.refine`
 * makes this a `ZodEffects`, which the API's `validateJson` wrapper accepts —
 * `zValidator` takes any `ZodType`.
 */
export const skipAccountingImportSchema = z
    .object({
    skip_reason: skipReasonEnum.optional(),
    duplicate: duplicateProvenanceSchema.optional(),
})
    .strict()
    .refine((b) => !b.duplicate || b.skip_reason === "duplicate", {
    message: "duplicate provenance requires skip_reason 'duplicate'",
    path: ["duplicate"],
});
// Pure-categorization save: project_id and fund_id may both be null (clear)
// or hold a uuid. `.strict()` forbids any other field — the corresponding RPC
// only updates those two columns.
export const categorizeAccountingImportSchema = z
    .object({
    project_id: z.string().uuid().nullable(),
    fund_id: z.string().uuid().nullable(),
})
    .strict();
// ---------- commit_import_split ----------
/**
 * Match allocation — links the bank line to one ledger transaction at the
 * given allocated_amount. When `entity_type` + `entity_id` are set, the RPC
 * also records a payment against that AR/AP entity (Model B per
 * plans/bank-feed-matching-batch-split.md): the inline process_ap_payment /
 * process_ar_payment call creates a payment ledger txn whose id ends up in
 * `accounting_import_matches.payment_transaction_id`.
 *
 * The entity_type / entity_id pairing rule (both set or both omitted) lives
 * on the outer `commitImportSplitSchema` via `superRefine`, because
 * `z.discriminatedUnion` cannot accept a ZodEffects-wrapped member.
 */
export const splitMatchAllocationSchema = z
    .object({
    kind: z.literal("match"),
    allocated_amount: z.number().positive(),
    transaction_id: z.string().uuid(),
    entity_type: z.enum(["ap_invoice", "ar_invoice"]).nullable().optional(),
    entity_id: z.string().uuid().nullable().optional(),
})
    .strict();
/**
 * Add allocation — emits a new ledger entry against the offset account.
 * Bank-side residual transaction aggregates all add allocations (one bank
 * entry + one entry per add allocation).
 */
export const splitAddAllocationSchema = z
    .object({
    kind: z.literal("add"),
    allocated_amount: z.number().positive(),
    offset_account_id: z.string().uuid(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    // `min(1)` rejects empty strings. An empty description would otherwise
    // win the COALESCE fallback chain in commit_import_split's ledger-entry
    // INSERT and override the import-level description — accept null/absent
    // to let the import description show through, not an empty string.
    description: z.string().min(1).nullable().optional(),
    /** NL WKR classification (jurisdiction-gated in the UI; rides through to nl_wkr_sync_from_lines). */
    wkr_category: nlWkrCategorySchema.optional(),
})
    .strict();
/** Discriminated union over the two allocation kinds. */
export const splitAllocationSchema = z.discriminatedUnion("kind", [
    splitMatchAllocationSchema,
    splitAddAllocationSchema,
]);
/**
 * Request body for POST /api/v1/clearing/orgs/:orgId/imports/:id/split.
 * The RPC enforces sum-equals-bank-amount tolerance and the bulk of the
 * validation server-side; this just shapes the array on the wire and
 * pins the entity-pair invariant for match allocations.
 */
export const commitImportSplitSchema = z
    .object({
    allocations: z.array(splitAllocationSchema).min(1),
})
    .strict()
    .superRefine((data, ctx) => {
    data.allocations.forEach((alloc, idx) => {
        if (alloc.kind === "match") {
            const hasType = alloc.entity_type != null;
            const hasId = alloc.entity_id != null;
            if (hasType !== hasId) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "entity_type and entity_id must both be set or both omitted",
                    path: ["allocations", idx, "entity_type"],
                });
            }
        }
    });
});
// ---------- add_expense_from_import (headless quick Add Expense) ----------
/**
 * One expense line on the wire — exactly the shape the quick-Add hook derives
 * from the import row (useAddExpenseFromImport): a single line whose VAT is
 * already resolved client-side (account default → org default → zero) and
 * whose `unit_price` is the absolute bank amount (the mapper emits
 * Math.abs(signed_amount), so a negative value is always a bug).
 */
export const addExpenseFromImportLineItemSchema = z
    .object({
    expense_account_id: z.string().uuid(),
    description: z.string().min(1),
    quantity: z.number().positive(),
    unit_price: z.number().min(0),
    tax_rate: z.number().min(0),
    tax_rate_id: z.string().uuid().optional(),
    // TaxTreatment in apps/web/src/lib/taxCalculations.ts minus
    // 'non_reclaimable' on purpose: quick-Add only ever sends a numeric rate
    // (the full bank-feed dialog, where "VAT not reclaimable" can be picked,
    // posts through the expense route instead).
    tax_treatment: z.enum(["standard", "exempt", "reverse_charge", "zero_rated"]),
    tax_inclusive: z.boolean(),
    sort_order: z.number().int().min(0),
    /**
     * NL WKR classification staged in the Match/Categorize cell. Review fix:
     * this .strict() schema was the one line schema that never got the field,
     * so a staged category 400'd the whole quick-Add (verified by executing
     * the built schema — unrecognized_keys). Rides through
     * add_expense_from_import → create_expense_with_line_items →
     * nl_wkr_sync_from_lines like every sibling.
     */
    wkr_category: nlWkrCategorySchema.optional(),
})
    .strict();
/**
 * Request body for POST /api/v1/clearing/orgs/:orgId/imports/:id/add-expense.
 * The backend RPC (add_expense_from_import) runs preflight + create-expense +
 * link atomically, so a link failure rolls the expense back instead of
 * leaving an orphan.
 *
 * `expense_date` is a bare calendar day (`yyyy-MM-dd`) — the import mapper
 * now emits the staged row's `booking_date` verbatim. TRANSITION (one
 * release): the old datetime-local shape is still accepted so a stale web tab
 * open across the deploy keeps working; the RPC's cast handles either form.
 * Tighten to date-only (`/^\d{4}-\d{2}-\d{2}$/`) next release, alongside the
 * matching fields in `expenses.ts`, `accountsPayable.ts` and
 * `accountsReceivable.ts`.
 */
export const addExpenseFromImportSchema = z
    .object({
    business_id: z.string().uuid().optional(),
    custom_business_name: z.string().min(1).optional(),
    expense_date: z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/, {
        message: "Expected yyyy-MM-dd or yyyy-MM-dd'T'HH:mm",
    }),
    description: z.string().min(1),
    payment_account_id: z.string().uuid(),
    reference: z.string().max(255).optional(),
    project_id: z.string().uuid().optional(),
    fund_id: z.string().uuid().optional(),
    line_items: z.array(addExpenseFromImportLineItemSchema).min(1),
    /** Suggestion provenance stamp — see {@link suggestionProvenanceSchema}. */
    suggestion: suggestionProvenanceSchema.nullable().optional(),
})
    .strict()
    .superRefine((data, ctx) => {
    // A quick Add must always carry a payee: either a fuzzy-matched FK or the
    // raw counterparty name (isMissingBusinessInfo routes blank rows to the
    // info dialog before this endpoint is ever hit).
    if (!data.business_id && !data.custom_business_name) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "business_id or custom_business_name is required",
            path: ["business_id"],
        });
    }
});
// ---------- clearing batch (the bank feed's bulk actions) ----------
/**
 * Max ops per POST /api/v1/clearing-batch/orgs/:orgId/imports/batch. The web
 * queues a bulk bar's rows and sends them in requests of at most this many;
 * the envelope schema caps `ops` at the same number.
 */
export const CLEARING_BATCH_MAX_OPS = 50;
/**
 * One row of a batch: which single-row write to run on which import row.
 *
 * `body` is deliberately `unknown` here. The route `safeParse`s it per row
 * with the op's EXISTING single-row schema (`skipAccountingImportSchema` /
 * `applyAccountingImportSchema` / `commitImportSplitSchema` /
 * `addExpenseFromImportSchema`), so the batch and the single-row routes
 * validate identically. `unlink` has no single-row body (its route takes
 * none), and `delete` (the feed row's hard delete, `delete_accounting_import`
 * — pending/skipped lines only, the RPC refuses the rest) has no single-row
 * route at all, so the batch holds both to an empty strict object: `{}` or
 * absent.
 * Not a `discriminatedUnion` of full bodies: zod 3.25 cannot take ZodEffects
 * members (three of the four body schemas are refined), and envelope-level
 * validation would 400 the WHOLE request on one bad row — a bad body fails
 * only its own row (`code: 'invalid_body'`).
 */
export const clearingBatchOpSchema = z
    .object({
    op: z.enum(["skip", "apply", "split", "add_expense", "unlink", "delete"]),
    import_id: z.string().uuid(),
    body: z.unknown(),
})
    .strict();
/** Request body for POST /api/v1/clearing-batch/orgs/:orgId/imports/batch. */
export const clearingBatchSchema = z
    .object({
    ops: z.array(clearingBatchOpSchema).min(1).max(CLEARING_BATCH_MAX_OPS),
})
    .strict();
// ---------- bulk_insert_accounting_imports (bank-statement import) ----------
/**
 * Max rows per bulk-insert request. The frontend chunks a parsed statement
 * into requests of this size (reusing one import_batch_id across chunks), and
 * the schema caps `rows` at the same number. Kept modest because the per-row
 * `AFTER INSERT` audit trigger fires once per row and each fire runs
 * `verify_backend_token()` (a Vault decrypt) + `is_current_user_super_admin()`.
 */
export const BULK_IMPORT_CHUNK_SIZE = 500;
/**
 * One parsed bank-statement row on the wire. Parsing (iso20022.js / CSV) stays
 * in the browser; the client sends already-shaped rows. `client_id` is the
 * preview transaction id — echoed back in `results[]` so the complete screen
 * can attribute per-row status. `entry_reference` is computed client-side
 * (buildEntryReference) and `credit_debit_indicator` is already sign-resolved
 * (resolveImportIndicator with the card flip) — the backend stores both as-is.
 *
 * String lengths mirror the DB columns (`reference`/`end_to_end_id`
 * VARCHAR(255), `currency` VARCHAR(3)) so an over-length value fails as a
 * clean 400 at the boundary rather than aborting a whole set-based chunk in
 * Postgres. `currency` allows '' — CSV rows carry an empty currency today.
 *
 * `line_index` is the row's position in its source statement file (CSV parse
 * loop index / CAMT global entry counter) — the statement order the feed grid
 * and the account register sort by. It is `.nullable().optional()` and the
 * `.optional()` HALF IS LOAD-BEARING: this schema is `.strict()`, and a stale
 * browser tab loaded before this release omits the key entirely — with
 * `.nullable()` alone the key would be REQUIRED and every one of that tab's
 * 500-row chunks would 400. The RPC's `item ->> 'line_index'` yields NULL for
 * an absent key and for an explicit null alike, which is the "no statement
 * order" value. Co-deploy rule: ship the API BEFORE the web app — the reverse
 * skew (a new client sending the key to an old API) is what `.strict()`
 * rejects.
 *
 * `value_date` is the statement's value date (when funds actually became
 * available), a plain calendar day like `booking_date` and NOT an instant. It
 * carries the SAME `.nullable().optional()` shape for the SAME reason: no
 * browser import path sends it (the CAMT parser never reads `ValDt`, the CSV
 * parser has no column for it), only the Enable Banking sync does. An API build
 * that sends the field before the RPC restatement lands has it silently ignored
 * by the old `bulk_insert_accounting_imports` rather than erroring, so there is
 * no wire skew in either direction.
 */
export const bankImportRowSchema = z
    .object({
    client_id: z.string().min(1),
    booking_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
        message: "Expected yyyy-MM-dd",
    }),
    value_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Expected yyyy-MM-dd" })
        .nullable()
        .optional(),
    amount: z.number(),
    currency: z.string().max(3),
    credit_debit_indicator: z.enum(["credit", "debit"]),
    entry_reference: z.string().nullable().optional(),
    line_index: z.number().int().nonnegative().nullable().optional(),
    description: z.string().nullable().optional(),
    reference: z.string().max(255).nullable().optional(),
    related_party_name: z.string().nullable().optional(),
    related_party_account: z.string().nullable().optional(),
    remittance_info: z.string().nullable().optional(),
    end_to_end_id: z.string().max(255).nullable().optional(),
})
    .strict();
/**
 * Request body for POST /clearing/orgs/:orgId/imports/bulk. `import_batch_id`
 * is client-generated once per import run and reused across chunks so an
 * upload stays one batch for remove_import_batch. `source_file_name` is
 * nullable (a synthetic CSV document may have no file name).
 */
export const bulkInsertBankImportSchema = z
    .object({
    bank_account_id: z.string().uuid(),
    import_batch_id: z.string().uuid(),
    source_type: z.enum(["camt", "csv"]),
    source_file_name: z.string().nullable().optional(),
    rows: z.array(bankImportRowSchema).min(1).max(BULK_IMPORT_CHUNK_SIZE),
})
    .strict();
//# sourceMappingURL=clearing.js.map